/**
 * WS-Q / AUD-8 LB-12 — the two things an expiry now needs that a sign-out never did.
 *
 * 1. **A re-authenticate prompt.** An expiry used to drop the operator at a blank Login with no
 *    explanation, mid-run, having just deleted their workspace. It now keeps the workspace
 *    (see `signOut.ts`) and says so, with the email already filled in.
 * 2. **Account-change detection.** The content purge exists for exactly one case: a DIFFERENT
 *    customer signing in on this machine. That used to happen as a side effect of every expiry
 *    — which is both too often (an expiry is the same person) and not often enough (nothing
 *    fired when account B signed in straight after account A on a still-live session). It now
 *    fires here, on the login itself.
 */
import { create } from "zustand";
import { signOutEverywhere, type SignOutDeps } from "./signOut.js";

/**
 * The last customer id that signed in on this device.
 *
 * An IDENTIFIER, not a credential — the same class as the remembered email, and deliberately a
 * plain `localStorage` key rather than a `cache.ts` entry, because `clearAllCache()` runs on
 * every sign-out and this must survive one to be worth anything.
 */
export const LAST_CUSTOMER_KEY = "genz.session.last-customer";

function readLastCustomer(): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(LAST_CUSTOMER_KEY) : null;
  } catch {
    return null; // a browser with site data blocked: treat it as a first sign-in
  }
}

function writeLastCustomer(customerId: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(LAST_CUSTOMER_KEY, customerId);
  } catch { /* best-effort — a missed write costs one extra purge, never a leak */ }
}

/**
 * Record a successful sign-in, purging the previous account's workspace first when the customer
 * changed.
 *
 * Call this BEFORE the new session is installed: the purge clears the session store, so running
 * it afterwards would sign the arriving account straight back out.
 *
 * A missing previous id (fresh install, cleared site data) is NOT treated as a change — there is
 * no other account's content on the device to protect, and purging would only cost the arriving
 * operator a re-download of work they already have.
 */
export async function noteSignIn(customerId: string, deps: SignOutDeps = {}): Promise<void> {
  const previous = readLastCustomer();
  if (previous && previous !== customerId) {
    await signOutEverywhere(deps, { reason: "account-changed" });
  }
  writeLastCustomer(customerId);
}

/**
 * WHY the prompt is up. The two causes look identical to the operator and are not the same
 * thing, so they do not get the same sentence.
 *
 * `"expired"` — the refresh chain reached its end, on the server. Nothing is wrong with this
 *   machine, and saying "expired" is accurate.
 * `"unreadable-store"` — the sign-in saved on THIS device could not be read back at boot
 *   (a corrupt `genz_secret_*` record, a permissions fault, a machine restored from a backup
 *   without its device key). The session on the server may be perfectly alive. Telling that
 *   operator their session expired would send them looking in the wrong place — and if it
 *   keeps happening, "it logs me out again" is a bug report about the device store, not about
 *   the chain length.
 */
export type ReauthReason = "expired" | "unreadable-store";

interface ReauthState {
  /** True while an expiry is waiting for the operator to sign in again. */
  pending: boolean;
  /** The email that was signed in, so the form can prefill it read-only. */
  email?: string;
  /** Which cause is on screen. Decides the prompt's copy; defaults to `"expired"`. */
  reason: ReauthReason;
  /** An expiry happened (or the operator asked to re-authenticate from the expiry banner). */
  open(email?: string, reason?: ReauthReason): void;
  /** The prompt is satisfied or abandoned — back to the ordinary Login form. */
  dismiss(): void;
}

/**
 * In-memory only, deliberately: a restart has no session to resume, so it belongs at the
 * ordinary Login rather than at a prompt claiming a session it no longer has.
 */
export const useReauthStore = create<ReauthState>()((set) => ({
  pending: false,
  email: undefined,
  reason: "expired",
  open: (email, reason = "expired") => set((s) => ({ pending: true, email: email ?? s.email, reason })),
  dismiss: () => set({ pending: false, email: undefined, reason: "expired" }),
}));

/** The re-authenticate prompt's state, for the shell and the Login form. */
export function useReauthPrompt(): { pending: boolean; email?: string; reason: ReauthReason } {
  const pending = useReauthStore((s) => s.pending);
  const email = useReauthStore((s) => s.email);
  const reason = useReauthStore((s) => s.reason);
  return { pending, email, reason };
}
